import React, {useCallback, useEffect, useState} from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import {useNavigate} from 'react-router';
import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';

import TwoColumnsContainer from '../components/TwoColumnsContainer';
import IntroImage from '../assets/img/dg2.a32ccd93.jpg';
import TextFieldController from '../components/TextFieldController';
import CodeValidationSchema, {
  getDefaultCodeValidationValues,
} from '../schemas/CodeValidationSchema';
import {useLazyGetDocumentQuery} from '../api/DocViewerAPI';
import BaseButton from '../components/BaseButton';
import ViewerContainer from '../components/ViewerContainer';
import ErrorMessage from '../components/ErrorMessage';
import {GetQueryParameterByName} from '../helpers/Utils';

const CodeValidationForm = ({setDataHandler, setErrorHandler}) => {
  // const {executeRecaptcha} = useGoogleReCaptcha();
  const [isBusy, setIsBusy] = useState(false);

  const [getDocumentQuery, {error: queryError, data, isSuccess}] =
    useLazyGetDocumentQuery(
      {}, // _dc: 'TODO'
      {refetchOnMountOrArgChange: false},
    );

  // const [getDocumentQuery, {data: documentData, error: queryError, isSuccess}] =    usePostDocumentMutation();

  const {
    control,
    handleSubmit,
    reset: resetForm,
    formState: {errors: formErrors},
  } = useForm({
    mode: 'all', //  onChange | onBlur | onSubmit | onTouched | all
    resolver: yupResolver(CodeValidationSchema),
    defaultValues: {
      ...getDefaultCodeValidationValues({
        code: GetQueryParameterByName('c') || '', // code param. Ej.: ?c=R+zTORQ8Qk2/hOVutqPMsw==
      }),
    },
  });

  useEffect(() => {
    return () => {
      resetForm();
    };
  }, [resetForm]);

  // with Captcha
  // const handleCaptchaVerify = useCallback(async () => {
  //   if (!executeRecaptcha) {
  //     console.log('Execute recaptcha not yet available');
  //     return;
  //   }
  //
  //   return await executeRecaptcha('document');
  // }, [executeRecaptcha]);

  useEffect(() => {
    if (queryError) {
      console.log(queryError);

      let message = 'Ha ocurrido un error.';

      if (
        queryError.hasOwnProperty('status') &&
        Number.isInteger(queryError.status)
      ) {
        switch (queryError.status) {
          case 400:
            message = 'La solicitud no pudo ser procesada.';
            break;
          case 404:
            message = 'No se ha encontrado el documento.';
            break;
          case 410:
            message = 'El documento expiró.';
            break;
          default:
            break;
        }
      }

      setErrorHandler(message);
    }
  }, [queryError, setErrorHandler]);

  useEffect(() => {
    if (isSuccess) {
      setDataHandler(data);
    }
  }, [isSuccess, data, setDataHandler]);

  const onSubmit = useCallback(
    (data) => {
      setIsBusy(true);

      setErrorHandler(null);

      const params = {code: btoa(data['code'])}; // convert code to base64

      getDocumentQuery(params)
        // .unwrap()
        .finally(() => {
          setIsBusy(false);
        });

      // with Captcha
      // handleCaptchaVerify()
      //   .then((token) => {
      //     if (token) {
      //       const params = {code: btoa(data['code']), captcha: token};
      //
      //       return getDocumentQuery(params);
      //       // // .unwrap()
      //       // .then(() => {
      //       //   console.log('here');
      //       // })
      //       // .catch((e) => {
      //       //   console.error(e);
      //       // });
      //     } else {
      //       throw Error('Token not found');
      //     }
      //   })
      //   .catch(() => {})
      //   .finally(() => {
      //     setIsBusy(false);
      //   });
    },
    [setErrorHandler, getDocumentQuery, setIsBusy], // handleCaptchaVerify
  );

  const canSubmit = useCallback(() => true, []);
  // with Captcha
  // const canSubmit = useCallback(() => !!executeRecaptcha, [executeRecaptcha]);

  return (
    <Grid
      container
      spacing={0}
      component={'form'}
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        // backgroundColor: 'pink',
        // height: '100%',
        flex: 1,
      }}>
      <Grid item xs={10}>
        <TextFieldController
          name={'code'}
          label={'Código de Verificación'}
          control={control}
          errors={formErrors}
          required
          disabled={isBusy}
          autoFocus={true}
          autoComplete="off"
        />
      </Grid>

      <Grid
        item
        xs={12}
        mt={'auto'}
        display={'flex'}
        justifyContent={'flex-end'}
        alignItems={'flex-end'}
        sx={
          {
            // backgroundColor: 'pink',
          }
        }>
        <BaseButton
          loading={isBusy}
          variant="contained"
          type={'submit'}
          disabled={!canSubmit() || isBusy}
          sx={{width: 110}}>
          ENVIAR
        </BaseButton>
      </Grid>
    </Grid>
  );
};

const DataContainer = ({data, resetHandler}) => {
  const navigate = useNavigate();

  const onBrowseDocuments = () => navigate('/private');

  return (
    <Stack
      flex={1}
      spacing={0}
      sx={{
        // backgroundColor: 'pink',
        flex: 1,
      }}>
      <Box>
        <Typography component="div" display={'inline'} color={'#9F9F9F'}>
          Código de Verificación:{' '}
        </Typography>

        <Typography component="div" display={'inline'}>
          {data.code}
        </Typography>
      </Box>

      <Box mt={3}>
        <Typography component="div" display={'inline'} color={'#9F9F9F'}>
          Título:{' '}
        </Typography>

        <Typography component="div" display={'inline'}>
          {data.name}
        </Typography>
      </Box>

      <Box mt={3}>
        <Typography component="div" display={'inline'} color={'#9F9F9F'}>
          Última firma:{' '}
        </Typography>

        <Typography component="div" display={'inline'}>
          {data.updated_at || '-'}
        </Typography>
      </Box>

      <Box
        mt={'auto'}
        pt={6}
        display={'flex'}
        flexDirection={{
          xs: 'column',
          lg: 'row',
        }}
        alignItems={'center'}
        justifyContent={{
          xs: 'center',
          lg: 'space-evenly',
        }}
        flex={1}
        sx={
          {
            // backgroundColor: 'pink',
            // height: '100%',
          }
        }>
        <Button variant="contained" onClick={resetHandler}>
          VERIFICAR DOCUMENTO
        </Button>

        <Button
          variant="contained"
          onClick={onBrowseDocuments}
          sx={{
            marginTop: {
              xs: 2,
              lg: 0,
            },
          }}>
          VER MIS DOCUMENTOS
        </Button>
      </Box>
    </Stack>
  );
};

const PublicViewerPage = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  // const [token, setToken] = useState(null);

  const onReset = useCallback(() => {
    setData(null);
  }, [setData]);

  // const onVerify = useCallback(
  //   (token) => {
  //     setToken(token);
  //     console.log({token});
  //   },
  //   [setToken],
  // );

  return (
    <TwoColumnsContainer
      leftColumn={
        <Box
          sx={{
            // ml: 60,
            // backgroundColor: 'pink',
            height: '100%',
            maxWidth: 600,
            marginLeft: {md: 'auto'},
            position: 'relative',
            display: 'flex',
          }}
          justifyContent={{
            xs: 'center',
            // md: "flex-start"
          }}
          alignItems={{
            xs: 'center',
            // md: 'flex-start',
          }}>
          <Card
            variant="outlined"
            sx={{
              flex: 1,
              ml: 3,
              p: 2,
              mr: 3,
              minHeight: 422,
              display: 'flex',
              flexDirection: 'column',
              // width: 526,
              // backgroundColor: 'yellow',
            }}>
            <CardHeader
              // color="secondary.main"
              title={data ? 'Documento Verificado' : 'Verificar Documento'}
              titleTypographyProps={{
                variant: 'h6',
                fontWeight: 'bold',
                sx: {
                  // background: `linear-gradient(rgba(143, 198, 0, .71), rgba(143, 198, 0, .71)), url(${SmallIntroImage})`,
                  // backgroundSize: 'cover',
                  // backgroundRepeat: 'no-repeat',
                  // backgroundPosition: '0% 58%;',
                },
              }}
            />

            {error && (
              <Box px={2} mb={2}>
                <ErrorMessage error={error} />
              </Box>
            )}

            <CardContent
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
              }}>
              {data ? (
                <DataContainer data={data} resetHandler={onReset} />
              ) : (
                <CodeValidationForm
                  setDataHandler={setData}
                  setErrorHandler={setError}
                />
              )}
            </CardContent>
          </Card>
        </Box>
      }
      rightColumn={
        data ? (
          <ViewerContainer url={data['preview_url']} />
        ) : (
          <img src={IntroImage} width={'100%'} height={'100%'} alt={'Imagen'} />
        )
      }
      ignoreResponsive={!!data}
    />
  );
};

// with Captcha
// const PublicViewerPage = () => {
//   const [data, setData] = useState(null);
//   const [error, setError] = useState(false);
//   // const [token, setToken] = useState(null);
//
//   const onReset = useCallback(() => {
//     setData(null);
//   }, [setData]);
//
//   // const onVerify = useCallback(
//   //   (token) => {
//   //     setToken(token);
//   //     console.log({token});
//   //   },
//   //   [setToken],
//   // );
//
//   return (
//     <GoogleReCaptchaProvider
//       reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
//       language="es"
//       // useRecaptchaNet="[optional_boolean_value]"
//       // useEnterprise="[optional_boolean_value]"
//       scriptProps={{
//         async: false, // optional, default to false,
//         defer: false, // optional, default to false
//         appendTo: 'head', // optional, default to "head", can be "head" or "body",
//         nonce: undefined, // optional, default undefined
//       }}
//       container={
//         {
//           // optional to render inside custom element
//           // element: 'captcha-container2',
//           // parameters: {
//           // badge: 'bottomleft', // optional, default undefined
//           // theme: 'dark', // optional, default undefined
//           // }
//         }
//       }>
//       <TwoColumnsContainer
//         leftColumn={
//           <Box
//             sx={{
//               // ml: 60,
//               // backgroundColor: 'pink',
//               height: '100%',
//               maxWidth: 600,
//               marginLeft: {md: 'auto'},
//               position: 'relative',
//               display: 'flex',
//             }}
//             justifyContent={{
//               xs: 'center',
//               // md: "flex-start"
//             }}
//             alignItems={{
//               xs: 'center',
//               // md: 'flex-start',
//             }}>
//             <Card
//               variant="outlined"
//               sx={{
//                 flex: 1,
//                 ml: 3,
//                 p: 2,
//                 mr: 3,
//                 minHeight: 422,
//                 display: 'flex',
//                 flexDirection: 'column',
//                 // width: 526,
//                 // backgroundColor: 'yellow',
//               }}>
//               <CardHeader
//                 // color="secondary.main"
//                 title={data ? 'Documento Verificado' : 'Verificar Documento'}
//                 titleTypographyProps={{
//                   variant: 'h6',
//                   fontWeight: 'bold',
//                   sx: {
//                     // background: `linear-gradient(rgba(143, 198, 0, .71), rgba(143, 198, 0, .71)), url(${SmallIntroImage})`,
//                     // backgroundSize: 'cover',
//                     // backgroundRepeat: 'no-repeat',
//                     // backgroundPosition: '0% 58%;',
//                   },
//                 }}
//               />
//
//               {error && (
//                 <Box px={2} mb={2}>
//                   <ErrorMessage error={error} />
//                 </Box>
//               )}
//
//               <CardContent
//                 sx={{
//                   display: 'flex',
//                   // backgroundColor: 'yellow',
//                   flexDirection: 'column',
//                   flexGrow: 1,
//                 }}>
//                 {/*<GoogleReCaptcha*/}
//                 {/*  onVerify={onVerify}*/}
//                 {/*  // refreshReCaptcha={refreshReCaptcha}*/}
//                 {/*/>*/}
//
//                 {data ? (
//                   <DataContainer data={data} resetHandler={onReset} />
//                 ) : (
//                   <CodeValidationForm
//                     setDataHandler={setData}
//                     setErrorHandler={setError}
//                   />
//                 )}
//               </CardContent>
//             </Card>
//           </Box>
//         }
//         rightColumn={
//           data ? (
//             <ViewerContainer url={data['preview_url']} />
//           ) : (
//             <img
//               src={IntroImage}
//               width={'100%'}
//               height={'100%'}
//               alt={'Imagen'}
//             />
//           )
//         }
//         ignoreResponsive={!!data}
//       />
//     </GoogleReCaptchaProvider>
//   );
// };

export default PublicViewerPage;
